import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewEncapsulation, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { switchMap, finalize, takeUntil } from 'rxjs/operators';
import moment from 'moment';

import { 
  DataService, 
  WorkflowContextService, 
  ContractorService 
} from 'src/app/services';
import { 
  ItemSearchOptionField, 
  ItemSearchOptionFieldOption, 
  ItemSearchUtils 
} from 'src/app/components/filter-list/models/filterClasses';
import { 
  FilterBuilderOutput, 
  translateFilterBuilderToLegacy 
} from 'src/app/models/filter-builder';
import { WorkflowJanusStatusPillComponent } from '../applicant/my-applications/my-applications-status-pill.component';

@Component({
  selector: 'app-public-view',
  standalone: false,
  templateUrl: './public-view.component.html',
  styleUrl: './public-view.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class PublicViewComponent implements OnInit, OnDestroy, AfterViewInit {
  isLoading = false;
  loadingDataForApplication = false;
  
  clientId: string | null = '3a9f4f35-f3ad-4ad5-8468-064555c899db';

  // Dynamic Jurisdiction Name Getter
  get jurisdictionName(): string {
    return this._context?.client?.name || 'Anytown';
  }

  // Dynamic Department State
  departments: any[] = [];
  activeDepartmentId: string | null = null; 

  // Pagination & Data State
  public page = 1;
  public submitPage = 0;
  public pageSize = 10;
  public pageCount = 1;
  public recordCount = 0;
  public totalRecords = 0;
  public data: any[] = [];
  
  private readonly PAGE_SIZE_STORAGE_KEY = 'wm-common-table-page-size-public-view';

  // Sorting State
  public sortField = 'dateCreated';
  public sortDescending = true;
  isSortOpen = false;
  selectedSortLabel = 'Most Recent';
  selectedSortOption: string | null = 'Most Recent';
  SortByOptions = ['All', 'Town (alphabetical)', 'Permit Type', 'Most Recent', 'Incomplete'];
  
  @ViewChild('sortWrapper') sortWrapper!: ElementRef;
  
  // Chevron implementation
  @ViewChild('deptContainer') deptContainer!: ElementRef<HTMLElement>;
  showBackward = false;
  showForward = false;
  moveForwardIcon = 'assets/images/move-forward.svg';
  moveBackwardIcon = 'assets/images/move-backward.svg';

  // Filter State
  public filtersReady = false;
  filterCriteria: any[] = [];
  filterTypes = [{ name: 'contains' }, { name: 'is' }, { name: 'in' }, { name: 'range' }];
  public filterOptions: ItemSearchOptionField[] = [];
  workflowOpts: any[] = [];

  // Observables
  private destroy$ = new Subject<void>();
  requestDataEvent: Subject<boolean> = new Subject<boolean>();

  // Data Table Configurations
  columns = [
    { key: 'applicationNumber', label: 'App No', headerType: 'text', cellType: 'text' },
    { key: 'dateCreated', label: 'App Date', headerType: 'text', cellType: 'text' },
    { key: 'issuedOn', label: 'Issue Date', headerType: 'text', cellType: 'text' },
    { key: 'parcelAddress', label: 'Site Address', headerType: 'text', cellType: 'text' },
    { key: 'applicantsName', label: 'Applicant', headerType: 'text', cellType: 'text' },
    { key: 'permitType', label: 'App Type', headerType: 'text', cellType: 'text' },
    { key: 'permitNumber', label: 'Permit Number', headerType: 'text', cellType: 'text' },
    {
      key: 'inspectionStatus',
      label: 'Inspection',
      headerType: 'text',
      cellType: 'component',
      component: WorkflowJanusStatusPillComponent,
      componentInputs: (row: any) => ({
        row: {
          inspectionStatus: row.inspectionStatus
        }
      })
    }
  ];

  constructor(
    private dataService: DataService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    public _context: WorkflowContextService,
    private _contractorSvc: ContractorService
  ) {}

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (this.isSortOpen && this.sortWrapper && !this.sortWrapper.nativeElement.contains(event.target)) {
      this.isSortOpen = false; 
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.updateArrowVisibility();
  }

  async ngOnInit() {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);
    if (storedPageSize) {
      const pageSize = Number(storedPageSize);
      if ([10, 25, 100, 200].includes(pageSize)) {
        this.pageSize = pageSize;
      }
    }

    this.route.parent?.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const routeClientId = params.get('clientId');
      this.clientId = routeClientId || this.clientId;
      
      if (this.clientId) {
        if (this._context.client) {
          this._context.client.id = this.clientId;
        }
        this.loadDepartments(this.clientId);
      }
    });

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const pageParam = params.get('p');
      if (pageParam) {
        this.submitPage = Math.max(0, Number(pageParam) - 1);
        if (this.data.length === 0) {
          this.isLoading = true;
        } else {
          this.loadingDataForApplication = true;
        }
        this.requestDataEvent.next(true);
      } else {
        this.submitPage = 0;
      }
    });

    await this.initFilters();
    this.initDataSubscription();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.updateArrowVisibility();
    }, 200);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDepartments(clientId: string) {
    this.isLoading = true;
    
    this.dataService.getClientDepartments(clientId).subscribe({
      next: (res: any[]) => {
        this.departments = res.map((d: any) => ({
          id: d.id,
          label: d.name || d.departmentName,
          icon: this.getDepartmentIcon(d.name || d.departmentName)
        }));

        if (this.departments.length > 0) {
          this.activeDepartmentId = this.departments[0].id;
        }

        if (this.data.length === 0) this.isLoading = true;
        this.requestDataEvent.next(true);
        
        setTimeout(() => this.updateArrowVisibility(), 100);
      },
      error: (err) => {
        console.error('Failed to load departments', err);
        if (this.data.length === 0) this.isLoading = true;
        this.requestDataEvent.next(true);
      }
    });
  }

  // --- Department Tabs Scroll Handling ---
  onDepartmentScroll() {
    this.updateArrowVisibility();
  }

  updateArrowVisibility() {
    const container = this.deptContainer?.nativeElement;
    if (!container) return;

    const isDesktop = window.innerWidth > 768;

    // Based on requirement: show chevrons on desktop only after 6 departments
    if (isDesktop && this.departments.length <= 6) {
      this.showBackward = false;
      this.showForward = false;
      return;
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const tolerance = 2;

    this.showBackward = container.scrollLeft > tolerance;
    this.showForward = container.scrollLeft < maxScrollLeft - tolerance;
  }

  scrollDepartments(direction: 'left' | 'right') {
    const container = this.deptContainer?.nativeElement;
    if (!container) return;
    
    const scrollAmount = container.clientWidth / 2; // scroll half a page
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  }
  // ---------------------------------------

  private getDepartmentIcon(deptName: string): string {
    if (!deptName) return 'business';
    const lowerName = deptName.toLowerCase();
    if (lowerName.includes('health')) return 'health_and_safety';
    if (lowerName.includes('plan')) return 'assignment';
    if (lowerName.includes('fire')) return 'local_fire_department';
    if (lowerName.includes('dog')) return 'pets';
    if (lowerName.includes('concom') || lowerName.includes('park')) return 'park';
    return 'business';
  }

  selectDepartment(deptId: string) {
    if (this.activeDepartmentId === deptId) return; 
    
    this.activeDepartmentId = deptId;
    this.submitPage = 0; 
    this.data = []; 
    
    this.isLoading = true;
    this.loadingDataForApplication = false;
    
    this.requestDataEvent.next(true);
  }

  private initDataSubscription(): void {
    this.requestDataEvent.pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        if (this.data.length === 0) {
          this.isLoading = true;
          this.loadingDataForApplication = false;
        } else {
          this.isLoading = false;
          this.loadingDataForApplication = true;
        }
        
        let filter = ItemSearchUtils.mergeFilters([...this.filterOptions], []);

        if (this.activeDepartmentId) {
          filter.push(new ItemSearchOptionField({
            title: 'departmentId',
            options: [new ItemSearchOptionFieldOption({ 
              title: 'departmentId',
              selected: true, 
              filterText: this.activeDepartmentId, 
              searchText: this.activeDepartmentId, 
              strOperator: 'is', 
              searchOptionMethod: 'dynamicFilter'
            })]
          }));
        }

        return this.dataService.getAllWorkflowApplicationsAnonymous(
          null as any,
          this.sortField,
          this.sortDescending,
          filter,
          this.submitPage + 1,
          this.pageSize,
          null as any,
          true
        ).pipe(
          finalize(() => {
            this.isLoading = false;
            this.loadingDataForApplication = false;
            this.cdr.detectChanges();
          })
        );
      })
    ).subscribe({
      next: (response: any) => {
        if (!response || !response.workflowApplications?.length) {
          this.data = [];
          this.recordCount = 0;
          this.totalRecords = 0;
          this.pageCount = 1;
          return;
        }

        this.page = this.submitPage;
        this.pageCount = response.pageCount;
        this.recordCount = response.recordCount;
        this.totalRecords = response.recordCount || 0;

        this.data = response.workflowApplications.map((wa: any) => ({
          applicationId: wa.appId,
          applicationNumber: wa.applicationNumber || '-',
          dateCreated: wa.dateCreated ? moment(wa.dateCreated).format('MM/DD/YYYY') : '-',
          issuedOn: wa.issuedOn ? moment(wa.issuedOn).format('MM/DD/YYYY') : '-',
          parcelAddress: wa.parcelAddress || '-',
          applicantsName: wa.applicantsName || '-',
          permitType: wa.permitType || '-',
          permitNumber: wa.permitNumber || wa.applicationNumber || '-', 
          inspectionStatus: this.formatInspectionStatus(wa.inspectionStatus)
        }));
      },
      error: (err) => {
        console.error('Failed to load public data table', err);
        this.isLoading = false;
        this.loadingDataForApplication = false;
      }
    });
  }

  private formatInspectionStatus(status: string | null | undefined): string {
    const value = status?.toString().trim().toLowerCase();

    switch (value) {
      case 'complete': return 'Success';
      case 'needed': return 'Action Required';
      case 'na': case 'n/a': case null: case undefined: case '': return 'N/A';
      default: return status || '-';
    }
  }

  initFilters(): Promise<void> {
    const workflows$ = this._context.client
      ? this.dataService.getWorkflows(this._context.client, false)
      : this.dataService.getPublicWorkflows(null, false);

    return workflows$.toPromise().then((workflows: any) => {
      if (workflows?.length) {
        this.workflowOpts = workflows.map((wf: any) => ({
          name: wf.version?.name ?? wf.name,
          value: wf.id
        })).sort((a: any, b: any) => a.name.localeCompare(b.name));
      }

      this.filterCriteria = [
        { name: 'Application Number', id: 'applicationNumber' },
        { name: 'Permit Type', id: 'permitType', types: ['is', 'in'], options: [...this.workflowOpts] },
        { name: 'Site Address', id: 'parcelAddress' },
        { name: 'Applicant Name', id: 'applicantsName' },
        { name: 'Issue Date', id: 'issuedOn', inputType: 'date', types: ['is', 'range'] }
      ];

      this.filtersReady = true;
      this.cdr.detectChanges();
    });
  }

  filtersChanged(filters: FilterBuilderOutput) {
    if (!this.filtersReady) return;
    let legacyFilters: ItemSearchOptionField[] = translateFilterBuilderToLegacy(filters);
    
    const simpleSearchOption = legacyFilters.flatMap(f => f.options ?? []).find(o => o.searchOptionMethod === 'simpleSearch' && o.filterText);
    const simpleSearchValue = simpleSearchOption?.filterText?.trim();

    if (simpleSearchValue) {
      const appNumberFilter = legacyFilters.find(f => f.title === 'applicationNumber');
      const permitTypeFilter = legacyFilters.find(f => f.title === 'permitType');

      if (appNumberFilter) {
        appNumberFilter.options = [new ItemSearchOptionFieldOption({ selected: true, filterText: simpleSearchValue, searchText: simpleSearchValue, searchOptionMethod: 'simpleSearch' })];
      } else {
        legacyFilters.push(new ItemSearchOptionField({
          title: 'applicationNumber',
          options: [new ItemSearchOptionFieldOption({ selected: true, filterText: simpleSearchValue, searchText: simpleSearchValue, searchOptionMethod: 'simpleSearch' })]
        }));
      }

      if (permitTypeFilter) {
        permitTypeFilter.options = [new ItemSearchOptionFieldOption({ selected: true, filterText: simpleSearchValue, searchText: simpleSearchValue, searchOptionMethod: 'simpleSearch' })];
      } else {
        legacyFilters.push(new ItemSearchOptionField({
          title: 'permitType',
          options: [new ItemSearchOptionFieldOption({ selected: true, filterText: simpleSearchValue, searchText: simpleSearchValue, searchOptionMethod: 'simpleSearch' })]
        }));
      }
    }

    this.filterOptions = legacyFilters;
    this.data = [];
    this.isLoading = true;
    this.requestDataEvent.next(true);
  }

  onPageChange(page: number): void {
    this.submitPage = page;
    this.router.navigate([], { relativeTo: this.route, queryParams: { p: page + 1 }, queryParamsHandling: 'merge', replaceUrl: true });
    
    this.data = [];
    this.isLoading = true;
    this.requestDataEvent.next(true);
  }

  onRowsPerPageChange(size: number): void {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.pageSize = pageSize;
    this.data = [];
    this.isLoading = true;
    this.requestDataEvent.next(true);
  }

  onSortChanged(event: { sortField: string | null; sortDescending: boolean | null; }): void {
    this.sortField = event.sortField || 'dateCreated';
    this.sortDescending = event.sortDescending ?? true;
    
    this.data = [];
    this.isLoading = true;
    this.requestDataEvent.next(true);
  }

  toggleSort(event: MouseEvent) {
    event.stopPropagation(); 
    this.isSortOpen = !this.isSortOpen;
  }

  selectSort(option: string) {
    this.selectedSortOption = option;
    this.selectedSortLabel = option;
    this.isSortOpen = false;

    switch (option) {
      case 'All': this.sortField = 'all'; this.sortDescending = false; break;
      case 'Town (alphabetical)': this.sortField = 'town'; this.sortDescending = false; break;
      case 'Permit Type': this.sortField = 'permitType'; this.sortDescending = false; break;
      case 'Most Recent': this.sortField = 'dateCreated'; this.sortDescending = true; break;
      case 'Incomplete': this.sortField = 'incomplete'; this.sortDescending = false; break;
    }
    
    this.data = [];
    this.isLoading = true;
    this.requestDataEvent.next(true);
  }
}